// Единственное место, которое трогает window.Telegram.WebApp (docs/07-miniapp-ux.md §1,
// docs/09-code-standards.md §8): весь остальной код витрины тестируется без Telegram.
// SDK подключается скриптом https://telegram.org/js/telegram-web-app.js в index.html —
// глобальный объект появляется до первого рендера React.

/** Цвета темы, которые Telegram передаёт клиенту (docs/07-miniapp-ux.md §1). */
export interface ThemeParams {
  bg_color?: string;
  secondary_bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
}

export type ColorScheme = "light" | "dark";
export type HapticImpactStyle = "light" | "medium" | "heavy" | "rigid" | "soft";
export type HapticNotificationType = "error" | "success" | "warning";

interface TelegramButton {
  show(): void;
  hide(): void;
  onClick(handler: () => void): void;
  offClick(handler: () => void): void;
}

interface TelegramMainButton extends TelegramButton {
  setText(text: string): void;
}

/**
 * TelegramContactResponse — второй аргумент колбэка requestContact, в той форме,
 * в которой его отдаёт telegram-web-app.js: подписанная строка в `response`
 * и она же разобранная в `responseUnsafe` (docs/11-backlog.md E8-19). Поля
 * необязательны: старые клиенты зовут колбэк вообще без второго аргумента.
 */
interface TelegramContactResponse {
  status?: string;
  response?: string;
  responseUnsafe?: { contact?: { phone_number?: string } };
}

/**
 * ContactResult — чем закончился запрос номера. Отказ покупателя и техническая
 * неудача разведены намеренно: отказ молчит, неудача показывает подсказку
 * (docs/07-miniapp-ux.md §2.5, docs/09-code-standards.md §8).
 */
export type ContactResult =
  | { status: "ok"; phone: string }
  | { status: "declined" }
  | { status: "failed" };

interface TelegramWebApp {
  initData: string;
  themeParams: ThemeParams;
  colorScheme: ColorScheme;
  ready(): void;
  expand(): void;
  disableVerticalSwipes(): void;
  onEvent(event: "themeChanged", handler: () => void): void;
  offEvent(event: "themeChanged", handler: () => void): void;
  showConfirm(message: string, callback: (confirmed: boolean) => void): void;
  // requestContact — не во всех клиентах Telegram (docs/07-miniapp-ux.md §2.5:
  // кнопка «Взять из Telegram» показывается, «если доступно»), поэтому метода
  // может не быть вовсе — не просто кинуть unsupported.
  requestContact?(callback: (granted: boolean, response?: TelegramContactResponse) => void): void;
  // isVersionAtLeast есть у всех актуальных клиентов, но у совсем старых его
  // может не быть — тогда и requestContact там не проходит.
  isVersionAtLeast?(version: string): boolean;
  HapticFeedback: {
    impactOccurred(style: HapticImpactStyle): void;
    notificationOccurred(type: HapticNotificationType): void;
  };
  BackButton: TelegramButton;
  MainButton: TelegramMainButton;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

function webApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

/** isAvailable — открыто ли приложение внутри Telegram. */
export function isAvailable(): boolean {
  return webApp() !== undefined;
}

/** ready сигнализирует Telegram, что интерфейс готов к показу. */
export function ready(): void {
  webApp()?.ready();
}

/** expand разворачивает Mini App на всю высоту экрана. */
export function expand(): void {
  webApp()?.expand();
}

/** disableVerticalSwipes выключает жест закрытия свайпом вниз на экранах со скроллом внутри. */
export function disableVerticalSwipes(): void {
  webApp()?.disableVerticalSwipes();
}

/** getInitData — сырая строка Telegram.WebApp.initData для обмена на JWT. Пустая вне Telegram. */
export function getInitData(): string {
  return webApp()?.initData ?? "";
}

/** getThemeParams — цвета текущей темы Telegram. Пустой объект вне Telegram. */
export function getThemeParams(): ThemeParams {
  return webApp()?.themeParams ?? {};
}

/** getColorScheme — light/dark клиента Telegram. light вне Telegram (безопасный дефолт). */
export function getColorScheme(): ColorScheme {
  return webApp()?.colorScheme ?? "light";
}

/** onThemeChanged подписывается на смену темы Telegram. Возвращает функцию отписки. */
export function onThemeChanged(callback: () => void): () => void {
  const app = webApp();
  if (!app) {
    return () => undefined;
  }
  app.onEvent("themeChanged", callback);
  return () => app.offEvent("themeChanged", callback);
}

/** showConfirm — системный диалог подтверждения (docs/07-miniapp-ux.md §1.6). */
export function showConfirm(message: string, onResult: (confirmed: boolean) => void): void {
  const app = webApp();
  if (!app) {
    onResult(false);
    return;
  }
  app.showConfirm(message, onResult);
}

// contactMinVersion — версия Bot API, с которой работает requestContact.
// В клиенте постарше метод в объекте есть, но запрос не проходит, поэтому
// наличия метода недостаточно (docs/11-backlog.md E8-19).
const contactMinVersion = "6.9";

/**
 * isRequestContactAvailable — пройдёт ли запрос номера в этом клиенте Telegram.
 * Проверяются и метод, и версия клиента: кнопку «Взять из Telegram» нельзя
 * показывать там, где запрос заведомо не сработает.
 */
export function isRequestContactAvailable(): boolean {
  const app = webApp();
  if (typeof app?.requestContact !== "function") {
    return false;
  }
  return app.isVersionAtLeast?.(contactMinVersion) === true;
}

/**
 * requestContact запрашивает у Telegram номер телефона покупателя
 * (docs/07-miniapp-ux.md §2.5, кнопка «Взять из Telegram»). `declined` —
 * покупатель отказал; `failed` — запрос не прошёл: клиент не поддерживает
 * его, приложение открыто не в Telegram или в ответе нет номера.
 */
export function requestContact(): Promise<ContactResult> {
  const app = webApp();
  if (!app?.requestContact) {
    return Promise.resolve({ status: "failed" });
  }
  return new Promise((resolve) => {
    app.requestContact?.((granted, response) => {
      if (!granted) {
        resolve({ status: "declined" });
        return;
      }
      const phone = contactPhone(response);
      resolve(phone === null ? { status: "failed" } : { status: "ok", phone });
    });
  });
}

/**
 * contactPhone достаёт номер из ответа клиента: сначала из разобранного
 * responseUnsafe, иначе — из url-encoded строки response, где контакт лежит
 * JSON-ом в параметре `contact`.
 */
function contactPhone(response?: TelegramContactResponse): string | null {
  const parsed = response?.responseUnsafe?.contact?.phone_number;
  if (parsed !== undefined && parsed !== "") {
    return parsed;
  }
  return phoneFromQuery(response?.response);
}

function phoneFromQuery(raw?: string): string | null {
  if (raw === undefined || raw === "") {
    return null;
  }
  const contact = new URLSearchParams(raw).get("contact");
  if (contact === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(contact);
    const phone = (parsed as { phone_number?: unknown } | null)?.phone_number;
    return typeof phone === "string" && phone !== "" ? phone : null;
  } catch {
    // Клиент прислал не JSON — номера у нас нет, это техническая неудача.
    return null;
  }
}

/** hapticFeedback — тактильный отклик на добавление в корзину, заказ, ошибку. */
export const hapticFeedback = {
  impactOccurred(style: HapticImpactStyle): void {
    webApp()?.HapticFeedback.impactOccurred(style);
  },
  notificationOccurred(type: HapticNotificationType): void {
    webApp()?.HapticFeedback.notificationOccurred(type);
  },
};

function button(pick: (app: TelegramWebApp) => TelegramButton) {
  return {
    show(): void {
      const app = webApp();
      if (app) pick(app).show();
    },
    hide(): void {
      const app = webApp();
      if (app) pick(app).hide();
    },
    onClick(handler: () => void): () => void {
      const app = webApp();
      if (!app) {
        return () => undefined;
      }
      pick(app).onClick(handler);
      return () => pick(app).offClick(handler);
    },
  };
}

/** backButton — нативная кнопка назад, синхронизируется с роутером (docs/07-miniapp-ux.md §1.3). */
export const backButton = button((app) => app.BackButton);

/** mainButton — нативная кнопка действия экрана (docs/07-miniapp-ux.md §1.2). */
export const mainButton = {
  ...button((app) => app.MainButton),
  setText(text: string): void {
    webApp()?.MainButton.setText(text);
  },
};
